import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

interface SearchModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
	const [keyword, setKeyword] = useState("");
	const navigate = useNavigate();
	const { theme } = useTheme();

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
		}
		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, onClose]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		if (keyword.trim()) {
			navigate(`/events?keyword=${encodeURIComponent(keyword.trim())}`);
			onClose();
			setKeyword("");
		} else {
			navigate("/events");
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
			onClick={onClose}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

			{/* Modal Content */}
			<div
				className={`relative w-full max-w-2xl rounded-2xl shadow-2xl ${
					theme === "light" ? "bg-white" : "bg-gray-800"
				} p-6 animate-slideDown`}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Close Button */}
				<button
					onClick={onClose}
					className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
						theme === "light"
							? "hover:bg-gray-100 text-gray-600"
							: "hover:bg-gray-700 text-gray-400"
					}`}
					aria-label="閉じる"
				>
					<svg
						className="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>

				{/* Title */}
				<h2
					className={`text-2xl font-bold mb-6 ${
						theme === "light" ? "text-gray-900" : "text-white"
					}`}
				>
					イベントを検索
				</h2>

				{/* Search Form */}
				<form onSubmit={handleSearch} className="space-y-4">
					<div>
						<input
							type="text"
							value={keyword}
							onChange={(e) => setKeyword(e.target.value)}
							placeholder="イベント名、詳細、主催者名、会場名、住所で検索"
							className={`w-full px-6 py-4 rounded-lg text-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
								theme === "light"
									? "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
									: "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
							}`}
							autoFocus
						/>
					</div>

					{/* Action Buttons */}
					<div className="flex gap-3 justify-end">
						<button
							type="button"
							onClick={onClose}
							className={`px-6 py-3 rounded-lg font-medium transition-colors ${
								theme === "light"
									? "bg-gray-200 hover:bg-gray-300 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							キャンセル
						</button>
						<button
							type="submit"
							className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium transition-all"
						>
							検索
						</button>
					</div>
				</form>

				{/* Quick Links */}
				<div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
					<p
						className={`text-sm font-medium mb-3 ${
							theme === "light" ? "text-gray-700" : "text-gray-300"
						}`}
					>
						クイックアクセス
					</p>
					<div className="flex flex-wrap gap-2">
						<button
							onClick={() => {
								navigate("/events");
								onClose();
							}}
							className={`px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${
								theme === "light"
									? "bg-gray-100 hover:bg-gray-200 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							すべてのイベント
						</button>
						<button
							onClick={() => {
								navigate("/events?category=ロック");
								onClose();
							}}
							className={`px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${
								theme === "light"
									? "bg-gray-100 hover:bg-gray-200 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							ロック
						</button>
						<button
							onClick={() => {
								navigate("/events?category=ポップス");
								onClose();
							}}
							className={`px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${
								theme === "light"
									? "bg-gray-100 hover:bg-gray-200 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							ポップス
						</button>
						<button
							onClick={() => {
								navigate("/events?category=ジャズ");
								onClose();
							}}
							className={`px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${
								theme === "light"
									? "bg-gray-100 hover:bg-gray-200 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							ジャズ
						</button>
						<button
							onClick={() => {
								navigate("/events?category=クラシック");
								onClose();
							}}
							className={`px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${
								theme === "light"
									? "bg-gray-100 hover:bg-gray-200 text-gray-800"
									: "bg-gray-700 hover:bg-gray-600 text-gray-200"
							}`}
						>
							クラシック
						</button>
					</div>
				</div>
			</div>

			<style>{`
				@keyframes slideDown {
					from {
						opacity: 0;
						transform: translateY(-20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				.animate-slideDown {
					animation: slideDown 0.3s ease-out;
				}
			`}</style>
		</div>
	);
};

export default SearchModal;
